import React from "react";
import { Activity, X } from "lucide-react";

interface BatchStatusModalProps {
    isOpen: boolean;
    modalData: any;
    onClose: () => void;
}

const BatchStatusModal: React.FC<BatchStatusModalProps> = ({
    isOpen,
    modalData,
    onClose,
}) => {
    if (!isOpen || !modalData) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl relative max-h-[85vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2 mb-6 text-blue-600 border-b pb-3">
                    <Activity className="w-5 h-5" />
                    <h2 className="text-xl font-bold text-gray-900">
                        Batch Status Details
                    </h2>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400">
                            File Name
                        </label>
                        <p className="text-sm font-semibold text-gray-800 break-all">
                            {modalData.fileName || "N/A"}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <div>
                            <label className="text-xs font-medium text-gray-500 block mb-1">
                                Upload Status
                            </label>
                            <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${modalData.uploadStatus === "Failed"
                                        ? "bg-red-50 text-red-700 border border-red-200"
                                        : "bg-green-50 text-green-700 border border-green-200"
                                    }`}
                            >
                                {modalData.uploadStatus || "Unknown"}
                            </span>
                        </div>

                        <div>
                            <label className="text-xs font-medium text-gray-500 block mb-1">
                                Processing Status
                            </label>
                            <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${modalData.processingStatus === "Failed"
                                        ? "bg-red-50 text-red-700 border border-red-200"
                                        : "bg-green-50 text-green-700 border border-green-200"
                                    }`}
                            >
                                {modalData.processingStatus || "Unknown"}
                            </span>
                        </div>
                    </div>

                    {modalData.processingError && (
                        <div className="bg-red-50/50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
                            <div className="flex items-center gap-2 font-bold mb-1">
                                <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                                Processing Error
                            </div>
                            <p className="font-medium pl-3.5 text-red-700">
                                {modalData.processingError}
                            </p>
                        </div>
                    )}

                    <div className="space-y-2.5 pt-2 text-xs border-t border-gray-100">
                        <div className="flex justify-between items-center text-gray-600">
                            <span className="font-medium">Uploaded On</span>
                            <span className="font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                                {modalData.uploadedOn
                                    ? new Date(modalData.uploadedOn).toLocaleString()
                                    : "N/A"}
                            </span>
                        </div>

                        <div className="flex justify-between items-center text-gray-600">
                            <span className="font-medium">Processing Started</span>
                            <span className="font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                                {modalData.processingStartedOn
                                    ? new Date(
                                        modalData.processingStartedOn
                                    ).toLocaleString()
                                    : "N/A"}
                            </span>
                        </div>

                        <div className="flex justify-between items-center text-gray-600">
                            <span className="font-medium">Processing Completed</span>
                            <span className="font-mono text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                                {modalData.processingCompletedOn
                                    ? new Date(
                                        modalData.processingCompletedOn
                                    ).toLocaleString()
                                    : "N/A"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchStatusModal;